import React, { useState, useEffect, useCallback } from 'react';
import {
  getAvailableDrivers,
  sendRequest,
  getRequests,
  acceptRequest as acceptReq,
  declineRequest as declineReq,
  cancelRequest as cancelReq,
  getConnections,
  removeConnection,
} from '../utils/sharingService';
import ProfileImage from './ProfileImage';

const styles = {
  container: {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 8,
    padding: 20,
    marginBottom: 24,
  },
  sectionTitle: {
    fontFamily: 'var(--font-heading)',
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--color-secondary)',
    letterSpacing: 2,
    textTransform: 'uppercase',
    marginBottom: 12,
    marginTop: 20,
  },
  filterInput: {
    width: '100%',
    padding: '8px 12px',
    background: 'var(--color-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: 6,
    color: 'var(--color-text)',
    fontFamily: 'var(--font-body)',
    fontSize: 13,
    outline: 'none',
    boxSizing: 'border-box',
    marginBottom: 8,
  },
  selectRow: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
  },
  driverSelect: {
    flex: 1,
    padding: '10px 14px',
    background: 'var(--color-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: 8,
    color: 'var(--color-text)',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    outline: 'none',
  },
  card: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    background: 'var(--color-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: 6,
    marginBottom: 8,
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
    flex: 1,
  },
  userName: {
    fontFamily: 'var(--font-heading)',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--color-text)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  userDetail: {
    fontFamily: 'var(--font-body)',
    fontSize: 11,
    color: 'var(--color-text-dim)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  btnRow: {
    display: 'flex',
    gap: 6,
    flexShrink: 0,
    marginLeft: 12,
  },
  btn: {
    padding: '6px 12px',
    border: '1px solid var(--color-border)',
    borderRadius: 5,
    fontFamily: 'var(--font-heading)',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: 'uppercase',
    cursor: 'pointer',
    background: 'transparent',
    color: 'var(--color-text-dim)',
  },
  btnAccept: {
    background: 'rgba(46, 204, 113, 0.15)',
    borderColor: '#2ecc71',
    color: '#2ecc71',
  },
  btnDecline: {
    background: 'rgba(231, 76, 60, 0.12)',
    borderColor: '#e74c3c',
    color: '#e74c3c',
  },
  btnSend: {
    background: 'rgba(46, 134, 193, 0.15)',
    borderColor: '#2e86c1',
    color: '#2e86c1',
  },
  emptyText: {
    fontFamily: 'var(--font-body)',
    fontSize: 13,
    color: 'var(--color-text-dim)',
    fontStyle: 'italic',
    padding: '8px 0',
  },
  errorText: {
    fontFamily: 'var(--font-body)',
    fontSize: 12,
    color: '#e74c3c',
    marginTop: 6,
  },
};

export default function ConnectionsPanel({ onConnectionsChange }) {
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [connections, setConnections] = useState([]);
  const [error, setError] = useState('');
  const [loadingAction, setLoadingAction] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [reqData, connData, drivers] = await Promise.all([
        getRequests(),
        getConnections(),
        getAvailableDrivers(),
      ]);
      setIncoming(reqData.incoming);
      setOutgoing(reqData.outgoing);
      setConnections(connData);
      setAvailableDrivers(drivers);
    } catch (err) {
      console.error('Failed to load sharing data:', err);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAction = async (action, id) => {
    setLoadingAction(id);
    setError('');
    try {
      await action(id);
      await loadData();
      if (onConnectionsChange) onConnectionsChange();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleSendRequest = async () => {
    if (!selectedDriverId) return;
    setLoadingAction(selectedDriverId);
    setError('');
    try {
      await sendRequest(selectedDriverId);
      await loadData();
      setSelectedDriverId('');
      setFilterText('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleRevoke = async (userId) => {
    setLoadingAction(userId);
    setError('');
    try {
      await removeConnection(userId);
      await loadData();
      if (onConnectionsChange) onConnectionsChange();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAction(null);
    }
  };

  const formatDriverLabel = (d) => {
    const parts = [d.displayName || 'Unknown'];
    if (d.teamName) parts.push(`- ${d.teamName}`);
    return parts.join(' ');
  };

  // Filter the dropdown list based on search text
  const filteredDrivers = filterText.length > 0
    ? availableDrivers.filter((d) => {
        const searchable = [d.displayName, d.teamName].filter(Boolean).join(' ').toLowerCase();
        return searchable.includes(filterText.toLowerCase());
      })
    : availableDrivers;

  const renderUserCard = (user, actions) => (
    <div key={user.userId || user.id} style={styles.card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
        <ProfileImage uid={user.userId || user.id} profileImage={user.profileImage} size={32} />
        <div style={styles.userInfo}>
          <div style={styles.userName}>{user.displayName || user.email}</div>
          <div style={styles.userDetail}>
            {user.teamName || ''}{user.teamName && user.primaryCar ? ' \u2022 ' : ''}{user.primaryCar || ''}
          </div>
        </div>
      </div>
      <div style={styles.btnRow}>{actions}</div>
    </div>
  );

  return (
    <div style={styles.container}>
      {/* ── Find Drivers ── */}
      <div style={{ ...styles.sectionTitle, marginTop: 0 }}>Find Drivers</div>
      <input
        style={styles.filterInput}
        placeholder="Filter by name or team..."
        value={filterText}
        onChange={(e) => setFilterText(e.target.value)}
      />
      <div style={styles.selectRow}>
        <select
          style={styles.driverSelect}
          value={selectedDriverId}
          onChange={(e) => setSelectedDriverId(e.target.value)}
        >
          <option value="">-- Select a driver ({filteredDrivers.length} available) --</option>
          {filteredDrivers.map((d) => (
            <option key={d.userId} value={d.userId} disabled={d.hasPendingRequest}>
              {formatDriverLabel(d)}{d.hasPendingRequest ? ' (pending)' : ''}
            </option>
          ))}
        </select>
        <button
          style={{ ...styles.btn, ...styles.btnSend, padding: '10px 16px' }}
          onClick={handleSendRequest}
          disabled={!selectedDriverId || loadingAction === selectedDriverId}
        >
          {loadingAction === selectedDriverId ? '...' : 'Send Request'}
        </button>
      </div>
      {availableDrivers.length === 0 && (
        <div style={styles.emptyText}>No other drivers are available for sharing right now.</div>
      )}

      {/* ── Incoming Requests ── */}
      {incoming.length > 0 && (
        <>
          <div style={styles.sectionTitle}>Incoming Requests</div>
          {incoming.map((r) =>
            renderUserCard(r.from, <>
              <button
                style={{ ...styles.btn, ...styles.btnAccept }}
                onClick={() => handleAction(acceptReq, r.id)}
                disabled={loadingAction === r.id}
              >
                Accept
              </button>
              <button
                style={{ ...styles.btn, ...styles.btnDecline }}
                onClick={() => handleAction(declineReq, r.id)}
                disabled={loadingAction === r.id}
              >
                Decline
              </button>
            </>)
          )}
        </>
      )}

      {/* ── Outgoing Requests ── */}
      {outgoing.length > 0 && (
        <>
          <div style={styles.sectionTitle}>Outgoing Requests</div>
          {outgoing.map((r) =>
            renderUserCard(r.to,
              <button
                style={{ ...styles.btn, ...styles.btnDecline }}
                onClick={() => handleAction(cancelReq, r.id)}
                disabled={loadingAction === r.id}
              >
                Cancel
              </button>
            )
          )}
        </>
      )}

      {/* ── Currently Sharing With ── */}
      <div style={styles.sectionTitle}>Currently Sharing With ({connections.length})</div>
      {connections.length === 0 ? (
        <div style={styles.emptyText}>No connections yet. Select a driver above to get started.</div>
      ) : (
        connections.map((c) =>
          renderUserCard(c.user,
            <button
              style={{ ...styles.btn, ...styles.btnDecline }}
              onClick={() => handleRevoke(c.userId)}
              disabled={loadingAction === c.userId}
            >
              Revoke
            </button>
          )
        )
      )}

      {error && <div style={styles.errorText}>{error}</div>}
    </div>
  );
}
